import { TooltipProvider } from "@/components/ui/tooltip";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { DashboardContent } from "@/components/dashboard/DashboardContent";
import { useDashboardState, DashboardViewType } from "@/hooks/useDashboardState";
import DashboardMenu from "@/components/dashboard/DashboardMenu";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { MobileBottomNav } from "@/components/dashboard/MobileBottomNav";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useCallback, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  SidebarProvider,
  SidebarTrigger,
  SidebarInset,
  useSidebar
} from "@/components/ui/sidebar";
import { PasswordResetModal } from "@/components/auth/PasswordResetModal";

interface DashboardProps {
  initialView?: DashboardViewType;
  viewMode?: 'list' | 'detail';
}

const Dashboard = ({ initialView = "overview", viewMode = 'list' }: DashboardProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const location = useLocation();
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  
  console.log("Dashboard Props:", { initialView, viewMode, params });
  
  const {
    activeSubmenu,
    services,
    searchQuery,
    setSearchQuery,
    showCreateService,
    setShowCreateService,
    userCategories,
    profile,
    hasRequests,
    activeView,
    selectedSettingsSection,
    selectedRequestsView,
    handleViewChange,
    handleSettingsSectionChange,
    handleViewSite,
    handleWhatsAppShare,
    handleTelegramShare,
    toggleSubmenu,
    refreshServices,
    refreshCategories
  } = useDashboardState(initialView);

  useEffect(() => {
    if (activeView === "settings" && params.section === undefined && !location.search.includes('section=')) {
      navigate(`/dashboard/settings/general`, { replace: true });
    }
  }, [activeView, params, navigate, location.search]);

  // Modified refresh logic with debounce to avoid repeated refreshes
  const handleServicesRefresh = useCallback(() => {
    // Check if this is a one-time refresh from URL param
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('refresh') && activeView === "services") {
      console.log("Dashboard detected refresh param, refreshing services");
      refreshServices();
      
      // Clean up the URL by removing the refresh parameter
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('refresh');
      const newSearch = newSearchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      navigate(newUrl, { replace: true });
    }
  }, [location.search, activeView, refreshServices, navigate, location.pathname]);

  // Handle URL-based refresh requests
  useEffect(() => {
    handleServicesRefresh();
  }, [handleServicesRefresh]);

  // Refresh services when navigating to services tab
  useEffect(() => {
    if (activeView === "services") {
      console.log("Services tab activated, refreshing services");
      refreshServices();
    }
  }, [activeView, refreshServices]);

  // Check for password recovery state
  useEffect(() => {
    const passwordRecoveryPending = localStorage.getItem("passwordRecoveryPending");
    if (passwordRecoveryPending === "true") {
      console.log("Password recovery pending, showing reset modal");
      setShowPasswordReset(true);
      localStorage.removeItem("passwordRecoveryPending");
    }
  }, []);

  const { sidebarItems } = DashboardMenu({
    activeView,
    activeSubmenu,
    toggleSubmenu,
    handleViewChange,
    handleSettingsSectionChange
  });

  return (
    <TooltipProvider>
      <SidebarProvider>
        <div className="min-h-screen bg-white flex w-full overflow-hidden">
          {/* Only show sidebar on desktop */}
          <div className="hidden md:block">
            <DashboardSidebar 
              profile={profile}
              sidebarItems={sidebarItems}
              activeView={activeView}
            />
          </div>

          <SidebarInset className="w-full">
            <DashboardHeader 
              profile={profile}
            />

            <DashboardContent 
              activeView={activeView}
              profile={profile}
              searchQuery={searchQuery}
              userCategories={userCategories}
              showCreateService={showCreateService}
              selectedSettingsSection={selectedSettingsSection}
              selectedRequestsView={selectedRequestsView}
              hasRequests={hasRequests}
              services={services}
              viewMode={viewMode}
              setShowCreateService={setShowCreateService}
              onViewSite={handleViewSite}
              onWhatsAppShare={handleWhatsAppShare}
              onSettingsSectionChange={handleSettingsSectionChange}
              refreshServices={refreshServices}
              refreshCategories={refreshCategories}
              className="pb-16 md:pb-0" // Add bottom padding on mobile for the navigation bar
            />
          </SidebarInset>
        </div>
        
        {/* Mobile Bottom Navigation */}
        <MobileBottomNav 
          activeView={activeView} 
          onViewChange={handleViewChange}
          activeSubmenu={activeSubmenu}
          toggleSubmenu={toggleSubmenu}
        />

        {/* Password Reset Modal */}
        <PasswordResetModal 
          isOpen={showPasswordReset}
          onClose={() => setShowPasswordReset(false)}
        />
      </SidebarProvider>
    </TooltipProvider>
  );
};

export default Dashboard;
